/**
 * 스타일 정리(AI) 호출 문지기 — «입력이 끝났을 때만» 부른다(2026-10-15 · 스타일 정리 429 재발 5회째).
 *
 * 🩸 종전에는 글자·칩이 바뀔 때마다 350ms 디바운스로 AI 를 불렀다. 천천히 치는 학생은 글자 하나마다
 *    한 번씩 불러, 한 기기가 1분에 26회를 보내고(10/13) 서버 제한(참가자당 분당 20회)에 걸려 429 가 났다.
 *    서버 제한은 그대로 두고, 클라이언트가 덜 부르게 고친다.
 *
 * 규칙:
 * - `invalidate()` = 입력이 바뀌었다. 옛 결과와 진행 중 요청을 버린다(요청은 취소). **AI 는 부르지 않는다.**
 * - `request()` = 입력이 끝났다(blur · 「노래 만들기」). 지금 입력의 결과가 없을 때만 1번 부른다.
 *   이미 같은 입력으로 가는 중이면 새로 부르지 않고 그 요청을 기다린다 — blur 와 버튼 누름이 겹쳐도 1번이다.
 */
export interface StyleCompileGate<T> {
  invalidate(): void;
  request(): Promise<T | null>;
  /** 입력이 바뀔 때마다 1씩 오른다 — 화면이 «이 결과가 아직 최신인가» 를 대조할 때 쓴다. */
  version(): number;
}

export function createStyleCompileGate<T>(
  run: (signal: AbortSignal, isCurrent: () => boolean) => Promise<T | null | undefined>,
): StyleCompileGate<T> {
  let version = 0;
  let done: { version: number; value: T } | null = null;
  let inflight: { version: number; promise: Promise<T | null>; controller: AbortController } | null = null;

  return {
    invalidate() {
      version += 1;
      done = null;
      inflight?.controller.abort();
      inflight = null;
    },
    request() {
      if (done && done.version === version) return Promise.resolve(done.value);
      if (inflight && inflight.version === version) return inflight.promise;
      const mine = version;
      const controller = new AbortController();
      const isCurrent = (): boolean => mine === version && !controller.signal.aborted;
      const promise: Promise<T | null> = run(controller.signal, isCurrent)
        .then((value) => {
          if (!isCurrent() || value === null || value === undefined) return null;
          done = { version: mine, value };
          return value;
        })
        .finally(() => {
          if (inflight?.promise === promise) inflight = null;
        });
      inflight = { version: mine, promise, controller };
      return promise;
    },
    version: () => version,
  };
}
